import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import SidebarMenu from '../components/SidebarMenu';
import Footer from '../components/Footer';
import ProfileMenu from '../components/ProfileMenu';
import NotificationBell from '../components/NotificationBell';
import MessageBell from '../components/MessageBell';
import { MarketingNav } from '../components/ui';
import { useEmails } from '../components/useEmails';
import axios from 'axios';
import { getMenuItems } from '../menuItems';
import { isEndUserRole } from '../utils/userRoles';
import { acme, acmeMarketingPageSx, acmeIconTones } from '../theme/acmeTheme';

function HomeLayout({ authenticated, user, handleLogout, isImpersonating = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const menuItems = getMenuItems(authenticated, user);

  const guestLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
  ];

  const authLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
  ];

  const toggleDrawer = (open) => () => setMobileOpen(open);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    if (authenticated) {
      axios
        .get('/api/mail/accounts', { withCredentials: true })
        .then((res) => {
          setAccounts(res.data || []);
          if (res.data?.length > 0) setSelectedAccount(res.data[0]);
        })
        .catch((err) => console.error('Failed to fetch accounts:', err));
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) fetchNotifications();
  }, [authenticated]);

  const { emailMessages, markEmailAsRead } = useEmails(authenticated, user, selectedAccount);

  const markNotificationAsRead = async (notificationId) => {
    try {
      const res = await axios.patch('/api/notifications/read', { notificationId });
      if (res.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n)),
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const authRightSlot = authenticated ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <NotificationBell
        notifications={notifications}
        onMarkAsRead={markNotificationAsRead}
        user={user}
        handleLogout={handleLogout}
        iconColor={acmeIconTones.default}
      />
      {!isEndUserRole(user?.role) && (
        <MessageBell
          messages={emailMessages}
          onMarkAsRead={markEmailAsRead}
          iconColor={acmeIconTones.default}
        />
      )}
      <ProfileMenu user={user} handleLogout={handleLogout} navTone="light" />
    </Box>
  ) : null;

  return (
    <Box sx={acmeMarketingPageSx}>
      <Box
        sx={{
          position: 'sticky',
          top: isImpersonating ? 48 : 0,
          zIndex: 1100,
          pt: 2,
          px: { xs: 1.5, md: 2 },
          backgroundColor: acme.colors.pageBg,
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <MarketingNav
            links={authenticated ? authLinks : guestLinks}
            authenticated={authenticated}
            rightSlot={authRightSlot}
          />
        </Box>
      </Box>

      {authenticated && (
        <SidebarMenu
          menuItems={menuItems}
          mobileOpen={mobileOpen}
          toggleDrawer={toggleDrawer}
          isAuthenticated={authenticated}
          handleLogout={handleLogout}
        />
      )}

      <Outlet
        context={{
          authenticated,
          user,
          handleLogout,
          notifications,
          markNotificationAsRead,
        }}
      />
      <Footer />
    </Box>
  );
}

export default HomeLayout;
